import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue, { defaultOptions } from 'primevue/config'
import { foodPreset } from '@/assets/js/Theme.js'
import App from './App.vue'
import router from './router'
import { i18n } from '@/plugins/i18n'
import { veeValidatePlugin } from '@/plugins/vee-validate'
import { registerCommonPrimeVue } from '@/plugins/primevue.common'
import { bootstrapAuth } from '@/app/bootstrapAuth'
import { bootstrapTelegram, bindTelegramRouter } from '@/telegram/bootstrapTelegram'
import { APP_ROLE } from '@/config/env'
import { initYandexMetrika } from 'yandex-metrika-vue3'
import { createGtm } from '@gtm-support/vue-gtm'
import { createGtag } from 'vue-gtag'
const isProduction = process.env.NODE_ENV === 'production'

import ToastService from 'primevue/toastservice'

import { setupHttpInterceptors } from '@/api/setupHttp'
import { applyRouteTitle } from '@/composables/usePageTitle'
import { registerPwa } from '@/registerPwa'
import { clearChunkReloadFlag, handleChunkLoadError } from '@/shared/lib/chunkLoad'
import { setAnalyticsContext } from '@/shared/lib/analytics'
import { bootstrapFacebookPixel } from '@/shared/lib/landingAnalytics'
import '@/assets/fonts/graphik.css'
import 'primeflex/primeflex.css'
import '@/assets/css/main.css'
import 'primeicons/primeicons.css'

window.addEventListener('unhandledrejection', (event) => {
    handleChunkLoadError(event.reason)
})

async function startApp() {
    const app = createApp(App)
    let tgContext = { isTgApp: false, initData: null, refCode: null }

    app.provide('appRole', APP_ROLE)

    app.use(createPinia())
    setupHttpInterceptors()
    app.use(i18n)
    app.use(veeValidatePlugin)
    app.use(ToastService)
    app.use(PrimeVue, {
        locale: {
            ...defaultOptions.locale,
            firstDayOfWeek: 1,
        },
        theme: {
            preset: foodPreset,
            options: {
                prefix: 'p',
                darkModeSelector: '.p-app-dark',
            },
        },
    })

    registerCommonPrimeVue(app)

    // VUE_APP_ROLE инлайняется на билде — ветка admin вырезается из non-admin сборок
    if (process.env.VUE_APP_ROLE === 'admin') {
        const { registerAdminPrimeVue } = require('@/plugins/primevue.admin')
        registerAdminPrimeVue(app)
    }

    try {
        tgContext = await bootstrapTelegram({ i18n })
        if (tgContext.isTgApp) {
            document.documentElement.classList.add('tg-app')
        }

        await bootstrapAuth({
            initData: tgContext.initData,
            refCode: tgContext.refCode,
        })
    } catch (error) {
        console.warn('[app] bootstrap failed', error)
    }

    app.use(router)
    try {
        await router.isReady()
        await bindTelegramRouter(router, tgContext)
    } catch (error) {
        console.warn('[app] router bootstrap failed', error)
        handleChunkLoadError(error)
    }

    if (isProduction) {
        console.log('Production mode')

        // FB
        bootstrapFacebookPixel(process.env.VUE_APP_FB_PIXEL_ID)

        app.use(initYandexMetrika, {
            id: 112054868,
            router: router,
            env: 'production',
            options: {
                clickmap: true,
                trackLinks: true,
                accurateTrackBounce: true,
                webvisor: true,
            },
        })
        app.use(createGtm({
            id: 'GTM-5WW72J58',
            vueRouter: router,
            enabled: true,
            debug: true,
        }))
        app.use(createGtag({
            tagId: 'G-Y3P55SRTDB',
            config: {
                send_page_view: true,
            },
        }))
        router.afterEach((to) => {
            if (typeof window.gtag !== 'function') {
                return
            }
            window.gtag('event', 'page_view', {
                page_title: document.title,
                page_path: to.fullPath,
                page_location: window.location.href,
            })
        })
    }

    setAnalyticsContext(app.config.globalProperties)

    app.mount('#app')
    clearChunkReloadFlag()
    registerPwa({ isTgApp: Boolean(tgContext.isTgApp) })

    watch(
        () => i18n.global.locale.value,
        (locale, prevLocale) => {
            document.documentElement.setAttribute('lang', locale === 'en' ? 'en' : 'ru')
            if (prevLocale != null && prevLocale !== locale) {
                applyRouteTitle(router.currentRoute.value)
            }
        },
        { immediate: true },
    )
}

startApp().catch((error) => {
    console.error('[app] start failed', error)
    handleChunkLoadError(error)
})
